import type { WheelThemeName } from '../../../../packages/wheel-of-fortune-5g/src/themes';
import App from '../../../app';
import { ComplianceActionTypes } from '../../../app/package/base/service/activation-flow/activation-flow-domain';
import { ModalType } from '../../../app/package/base/service/modals/modal-service';
import { LOCAL_STORAGE_KEY_TRACKED_MINI_GAMES } from '../../../constants';
import type { SubHelper } from '../../base/package/patterns/package/store';
import { localStorage_getOrNull, localStorage_set } from '../../base/package/util/localStorage';
import type { IncentiveMiniGame, IncentivePageView } from '../types';

export const MINI_GAME_OFFER_NAME_5G_WHEEL_DEFAULT = '5g-tipico-100fs-50fs-20fs-10fs';
export const MINI_GAME_OFFER_NAME_5G_WHEEL_SUMMER = '5g-wheel-summer-skin-game';

/**
 * Skin names served by the native wheel-of-fortune-5g component, mapped to the
 * artwork each one runs with. Any other skin goes to the Gamanza iframe.
 *
 * Keyed on the launch config's `skin.name`, not the offer name: the two can
 * diverge (an offer named `..._old` maps to a current skin), and the skin is
 * what actually determines the game. The host modal reads `skin.name` from a
 * post-claim launch peek (see `peekSkinName`), so the value is available before
 * the wheel boots.
 */
const _5G_WHEEL_SKIN_THEMES: Record<string, WheelThemeName> = {
  [MINI_GAME_OFFER_NAME_5G_WHEEL_DEFAULT]: 'metal',
  [MINI_GAME_OFFER_NAME_5G_WHEEL_SUMMER]: 'beach',
};

/** Whether the skin is one the native wheel serves. */
export function is5gWheelSkin(skinName: string | undefined): boolean {
  return !!skinName && skinName in _5G_WHEEL_SKIN_THEMES;
}

/** Artwork for a native-wheel skin; `undefined` for anything else. */
export function get5gWheelThemeName(skinName: string | undefined): WheelThemeName | undefined {
  return skinName ? _5G_WHEEL_SKIN_THEMES[skinName] : undefined;
}

function _readTrackedIds(): string[] {
  return localStorage_getOrNull<string[]>(LOCAL_STORAGE_KEY_TRACKED_MINI_GAMES) ?? [];
}

function _writeTrackedIds(ids: string[]): void {
  localStorage_set(LOCAL_STORAGE_KEY_TRACKED_MINI_GAMES, ids);
}

/** Mark a game as tracked (user has seen it). */
export function trackMiniGame(id: string): void {
  const ids = _readTrackedIds();
  if (!ids.includes(id)) {
    ids.push(id);
    _writeTrackedIds(ids);
  }
}

/** Whether the game has been tracked (user already saw/interacted with it). */
export function isMiniGameTracked(id: string): boolean {
  return _readTrackedIds().includes(id);
}

export function cleanupTrackedMiniGames(currentBackendIds: string[]): void {
  if (currentBackendIds.length === 0) return; // backend returned nothing — skip cleanup to avoid wiping state
  const ids = _readTrackedIds();
  const cleaned = ids.filter((id) => currentBackendIds.includes(id));
  if (cleaned.length !== ids.length) {
    _writeTrackedIds(cleaned);
  }
}

export interface PlayableMiniGamesResult {
  miniGame: IncentiveMiniGame | null;
  count: number;
}

/**
 * Fetches all mini-games from the backend, cleans up stale tracked IDs,
 * and returns games the user has previously dismissed (tracked). Untracked
 * games are only reachable via the forced modal — they don't appear as cards.
 */
export async function getPlayableMiniGames(): Promise<PlayableMiniGamesResult> {
  const allMiniGames = await window.$app.incentiveApi.getMiniGames();
  cleanupTrackedMiniGames(allMiniGames.map((g) => g.id));

  const cardGames = allMiniGames.filter((g) => isMiniGameTracked(g.id));

  return {
    miniGame: cardGames[0] || null,
    count: cardGames.length,
  };
}

export function shouldShowMiniGameModal(): boolean {
  const miniGame = window.$app.incentiveApi?.signal.miniGame.value;
  if (!miniGame) return false;
  if (isMiniGameTracked(miniGame.id)) return false;
  return App.instance.activationFlow.isActionEnabled(ComplianceActionTypes.GAMEPLAY);
}

export async function onMiniGameModalClose(id: string): Promise<void> {
  trackMiniGame(id);

  const allGames = await window.$app.incentiveApi.getMiniGames();
  const nextUntracked = allGames.find((g) => !isMiniGameTracked(g.id)) ?? null;
  window.$app.incentiveApi.signal.miniGame.next(nextUntracked, false);

  window.$app.incentiveApi.getActualBonuses();
}

/**
 * Subscribes to signal.miniGame and keeps the provided state in sync.
 * Call in connectedCallback; cleanup is handled by SubHelper.unsubscribeAll().
 */
export function subscribeMiniGameUpdates(
  subHelper: SubHelper,
  isConnected: () => boolean,
  getCount: () => number,
  onUpdate: (result: PlayableMiniGamesResult) => void,
) {
  subHelper.addSub(window.$app.incentiveApi.signal.miniGame, async (miniGame) => {
    if (!isConnected()) return;
    if (miniGame && isMiniGameTracked(miniGame.id)) {
      // Signal only carries one game — preserve existing count, floor at 1
      onUpdate({ miniGame, count: Math.max(getCount(), 1) });
    } else {
      const result = await getPlayableMiniGames();
      if (!isConnected()) return;
      onUpdate(result);
    }
  });
}

export interface OpenMiniGameModalOptions {
  miniGame: IncentiveMiniGame;
  openIncentiveModalViewOnFinish?: boolean;
  returnToView?: IncentivePageView;
}

export function openMiniGameModal(options: OpenMiniGameModalOptions): void {
  // The 5G modal is the single entry point for every Gamanza mini-game. The
  // native-wheel-vs-iframe decision needs the launch config's `skin.name`, which
  // only exists after the reward is claimed and a launch URL is minted — so the
  // choice cannot be made here, before claim. The modal makes it after claim and
  // renders the iframe itself when the skin is not one the native wheel serves.
  import('../../components/drawers/incentive-mini-game-5g-modal/ui-incentive-mini-game-5g-modal').then(
    ({ UiIncentiveMiniGame5gModal }) => {
      const modal = new UiIncentiveMiniGame5gModal();
      modal.miniGame = options.miniGame;
      modal.openIncentiveModalViewOnFinish = options.openIncentiveModalViewOnFinish ?? true;
      if (options.returnToView) {
        modal.returnToView = options.returnToView;
      }
      window.$app.modal.show(modal, ModalType.IncentiveModal);
    },
  );
}
